/**
 * EmptyState - Enhetlig tomtillstånd för listor och sektioner
 * Visar ikon, rubrik, beskrivning och valfri åtgärd
 */

import React from 'react';

export type EmptyStateVariant = 'default' | 'card' | 'primary';

export interface EmptyStateProps {
  /** Rubrik */
  title: string;
  /** Valfri beskrivning under rubriken */
  description?: string;
  /** Valfri ikon (t.ex. Heroicon) */
  icon?: React.ReactNode;
  /** Valfri åtgärd/CTA (t.ex. knapp) */
  action?: React.ReactNode;
  /** Minsta höjd (t.ex. h-64) */
  minHeight?: string;
  /** Variant: default = enkel, card = elevad karta, primary = blå accent */
  variant?: EmptyStateVariant;
  /** Ytterligare CSS-klasser */
  className?: string;
}

const VARIANT_CLASSES: Record<EmptyStateVariant, string> = {
  default: 'text-textSecondary',
  card: 'bg-bgElevated rounded-lg border border-border p-12 text-center',
  primary: 'bg-BgPrimaryLight dark:bg-blue-900/20 border border-border rounded-lg p-6 text-center',
};

const ICON_CLASSES: Record<EmptyStateVariant, string> = {
  default: 'text-textSecondary opacity-50',
  card: 'text-textSecondary',
  primary: 'text-blue-500',
};

const TITLE_CLASSES: Record<EmptyStateVariant, string> = {
  default: 'text-textDefault',
  card: 'text-textPrimary',
  primary: 'text-blue-900 dark:text-blue-100',
};

const DESC_CLASSES: Record<EmptyStateVariant, string> = {
  default: 'text-textSecondary',
  card: 'text-textSecondary',
  primary: 'text-blue-700 dark:text-blue-300',
};

export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon,
  action,
  minHeight = 'h-64',
  variant = 'default',
  className = '',
}) => {
  const wrapperClass =
    variant === 'default'
      ? `flex flex-col items-center justify-center ${minHeight} text-center ${className}`
      : `flex flex-col items-center justify-center ${minHeight} ${VARIANT_CLASSES[variant]} ${className}`;

  return (
    <div className={wrapperClass}>
      {icon && (
        <div className={`mb-4 flex justify-center ${ICON_CLASSES[variant]}`}>{icon}</div>
      )}
      <h3 className={`text-lg font-medium mb-2 ${TITLE_CLASSES[variant]}`}>{title}</h3>
      {description && <p className={`text-sm ${DESC_CLASSES[variant]} max-w-md`}>{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
};
